import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { gsap, useGSAP, EASE, prefersReducedMotion } from "../../lib/gsap";
import { featuredProjects, indexedProjects, projects } from "../../data/portfolio";
import { useFilter } from "../../context/FilterContext";
import Reveal from "../motion/Reveal";
import AxisReveal from "../motion/AxisReveal";
import Decode from "../motion/Decode";
import Ruler from "../motion/Ruler";
import MaskMedia from "../motion/MaskMedia";
import Marquee from "../motion/Marquee";
import ProjectMedia from "../ui/ProjectMedia";
import FieldCanvas from "../bg/FieldCanvas";
import Guides from "../bg/Guides";

/* Domains the work actually covers — the seam that opens the dark chapter. */
const DOMAINS = [
  "Multi-agent systems",
  "Retrieval-augmented generation",
  "Audio machine learning",
  "Time-series forecasting",
  "Applied cryptography",
  "Data engineering",
];

/* ── Featured specimen — full-bleed, alternating asymmetry ──────────── */
const Specimen = ({ project, flip }) => {
  const results = Object.entries(project.results || {});

  return (
    <article className="relative pt-16 md:pt-24">
      <div className="grid-12 items-end gap-y-8">
        {/* Media */}
        <div className={`col-span-12 ${flip ? "lg:col-span-7 lg:col-start-6" : "lg:col-span-8"}`}>
          <MaskMedia from={flip ? "right" : "left"}>
            <Link
              to={`/project/${project.id}`}
              className="media-frame media-hover block aspect-[16/10] overflow-hidden"
              data-cursor-label="Open"
              aria-label={`Open ${project.title} case study`}
            >
              <ProjectMedia project={project} sizes="(max-width: 768px) 100vw, 66vw" />
            </Link>
          </MaskMedia>

          {/* Specimen label — the plate is captioned like a record */}
          <div className="hair-t mt-2.5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 pt-2">
            <span className="t-label">
              Plate {project.index}
              <span className="hidden sm:inline">
                {" "}· {project.video ? "Interface recording" : "Reference image"}
              </span>
            </span>
            <span className="t-label">{project.status}</span>
          </div>
        </div>

        {/* Title + meta */}
        <div
          className={`col-span-12 ${
            flip ? "lg:col-span-5 lg:col-start-1 lg:row-start-1" : "lg:col-span-4"
          }`}
        >
          <div className="flex items-baseline gap-3">
            <span className="t-num text-xs text-flare">{project.index}</span>
            <Decode as="span" className="t-label" text={project.category} />
          </div>

          <AxisReveal as="h3" className="t-h2 mt-3 text-fg" wdth={104} wght={640} fromWdth={72}>
            <Link to={`/project/${project.id}`} className="link-wipe" data-cursor-label="Open">
              {project.title}
            </Link>
          </AxisReveal>

          <Reveal className="t-body mt-4 text-dim" distance={18}>
            {project.description}
          </Reveal>

          <Reveal stagger={0.06} className="mt-6 flex flex-wrap gap-x-4 gap-y-2">
            {project.techStack.map((item) => (
              <span key={item} data-reveal-item className="t-label text-fg">
                {item}
              </span>
            ))}
          </Reveal>
        </div>
      </div>

      {/* Readouts */}
      <Reveal stagger={0.07} className="hair-t mt-10 grid gap-x-8 gap-y-5 pt-5 sm:grid-cols-3">
        {results.map(([key, value]) => (
          <div key={key} data-reveal-item>
            <span className="t-label block">[ {key} ]</span>
            <span className="mt-1 block text-sm text-fg">{value}</span>
          </div>
        ))}
      </Reveal>
    </article>
  );
};

/* ── Index of remaining projects, with a parked preview ─────────────── */
const WorkIndex = ({ items }) => {
  const scope = useRef(null);
  const [active, setActive] = useState(null);

  useGSAP(
    () => {
      if (active === null || prefersReducedMotion()) return undefined;
      const plate = scope.current.querySelector(`[data-plate="${active}"]`);
      if (!plate) return undefined;

      const tween = gsap.fromTo(
        plate,
        { clipPath: "inset(0% 0% 100% 0%)" },
        { clipPath: "inset(0% 0% 0% 0%)", duration: 0.6, ease: EASE.out }
      );

      return () => tween.kill();
    },
    { scope, dependencies: [active] }
  );

  return (
    <div ref={scope} className="relative">
      <div className="relative">
        {/* Parked preview — desktop only, purely decorative */}
        <div
          className="pointer-events-none absolute right-0 top-0 hidden h-full w-[34%] lg:block"
          aria-hidden="true"
        >
          <div className="sticky top-[22vh] aspect-[4/3] w-full overflow-hidden">
            {items.map((project) => (
              <div
                key={project.id}
                data-plate={project.id}
                className="absolute inset-0"
                style={{
                  opacity: active === project.id ? 1 : 0,
                  transition: "opacity 200ms cubic-bezier(0.16,1,0.3,1)",
                }}
              >
                <img
                  src={project.image}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                  style={{ filter: "grayscale(1) contrast(1.1)" }}
                />
                <span
                  className="absolute inset-x-0 bottom-0 h-px"
                  style={{ background: "var(--flare)" }}
                />
              </div>
            ))}
          </div>
        </div>

        <ul className="lg:w-[62%]">
          {items.map((project) => (
            <li key={project.id}>
              <Link
                to={`/project/${project.id}`}
                className="hair-b group flex flex-col gap-4 py-7 md:flex-row md:items-baseline md:justify-between md:gap-8"
                onMouseEnter={() => setActive(project.id)}
                onFocus={() => setActive(project.id)}
                onMouseLeave={() => setActive(null)}
                onBlur={() => setActive(null)}
                data-cursor-label="Open"
              >
                <span className="flex items-baseline gap-4 md:gap-6">
                  <span className="t-num text-xs text-dim group-hover:text-flare">
                    {project.index}
                  </span>
                  <span
                    className="t-h3 text-fg transition-[font-variation-settings,color] duration-500 ease-out group-hover:text-flare group-focus-visible:text-flare"
                    style={{ fontVariationSettings: "'wdth' 100, 'wght' 600" }}
                    data-row-title
                  >
                    {project.title}
                  </span>
                </span>

                <span className="flex flex-col gap-1 md:items-end">
                  <span className="t-label">{project.category}</span>
                  <span className="t-mono text-dim">
                    {project.techStack.slice(0, 3).join(" · ")}
                  </span>
                </span>

                {/* Touch/tablet get the plate inline instead of parked */}
                <span className="media-frame block aspect-[16/9] w-full overflow-hidden lg:hidden">
                  <img
                    src={project.image}
                    alt={`${project.title} — ${project.category}`}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                    style={{ filter: "grayscale(1) contrast(1.1)" }}
                  />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const Work = () => {
  const { tech, clear } = useFilter();

  const matches = (project) => !tech || project.techStack.includes(tech);
  const shownFeatured = featuredProjects.filter(matches);
  const shownIndex = indexedProjects.filter(matches);
  const matchCount = shownFeatured.length + shownIndex.length;

  return (
    <section
      id="work"
      className="chapter chapter-dark relative pb-chapter pt-0"
      aria-labelledby="work-title"
    >
      {/* Living ground: the chapter's ink, moving. Sits under the hairline
          sheet and under every piece of content, stuck to the viewport so the
          bloom stays in frame down a chapter several screens tall. */}
      <div className="field-inset" aria-hidden="true">
        <div className="field-sticky">
          <FieldCanvas className="field-fill" />
          <Guides className="field-fill" />
        </div>
      </div>

      <div className="hair-b hair-t py-4">
        <Marquee items={DOMAINS} duration={54} className="t-label text-dim" />
      </div>

      <div className="page pt-chapter">
        <div className="flex flex-wrap items-baseline justify-between gap-4 pb-4">
          <h2 id="work-title" className="t-label">
            <span className="text-flare">03</span> — Work
            <span className="sr-only"> — selected projects</span>
          </h2>
          <Decode
            as="span"
            className="t-label"
            text={`${String(projects.length).padStart(2, "0")} projects · 02 in detail`}
          />
        </div>
        <Ruler ticks={20} />

        {/* Filter state, driven from the capability field */}
        <div
          className="mt-5 flex min-h-[44px] flex-wrap items-center gap-x-4 gap-y-2"
          role="status"
          aria-live="polite"
        >
          {tech ? (
            <>
              <span className="t-label">
                Filtering by <span className="text-flare">{tech}</span> — {matchCount} of{" "}
                {projects.length}
              </span>
              <button
                type="button"
                onClick={clear}
                className="t-label min-h-[44px] px-1 text-fg underline decoration-[var(--flare)] decoration-1 underline-offset-4"
                data-cursor-label="Clear"
              >
                Clear filter
              </button>
            </>
          ) : (
            <span className="t-label">Select a technology in the field below to filter</span>
          )}
        </div>

        {shownFeatured.map((project, i) => (
          <Specimen key={project.id} project={project} flip={i % 2 === 1} />
        ))}

        <div className={shownFeatured.length ? "mt-20 md:mt-28" : "mt-12"}>
          <div className="hair-b flex items-baseline justify-between pb-4">
            <span className="t-label">{shownFeatured.length ? "Also built" : "Matching work"}</span>
            <span className="t-label">
              {String(shownIndex.length).padStart(2, "0")} entr{shownIndex.length === 1 ? "y" : "ies"}
            </span>
          </div>

          {shownIndex.length > 0 ? (
            <WorkIndex items={shownIndex} />
          ) : (
            <p className="t-body py-10 text-dim">
              Nothing else in the index uses {tech} — the two specimens above cover it.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default Work;
